import React, { useState, useRef, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  FcHome,
  FcMenu,
  FcMoneyTransfer,
  FcContacts,
  FcBusinessContact,
  FcSettings,
  FcStatistics,
  FcGlobe,
  FcImageFile,
  FcViewDetails
} from "react-icons/fc";
import { GlobalData } from "../../../assets/data/GlodalData";
import { IoMdLogOut } from "react-icons/io";
import { useAuth } from "../../../hooks/AuthContext";

const AdminNavbar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const sidebarRef = useRef(null);
  const { user, logout } = useAuth();

  const [openDropdown, setOpenDropdown] = useState(null);

  const handleLogout = () => {
    logout();
    navigate("/auth/login");
  };

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const closeSidebar = () => {
    setIsSidebarOpen(false);
  };

  const toggleDropdown = (index) => {
    setOpenDropdown(openDropdown === index ? null : index);
  };

  // Define nav sections for Shishu Hriday
  const allNavSections = [
    {
      title: "Overview",
      links: [
        {
          name: "Admin Dashboard",
          description: "Summary of foundation activities",
          path: "/auth/dashboard",
          icon: <FcHome className="text-lg" />,
        },
      ],
    },
    {
      title: "Management",
      links: [
        {
          name: "Donation Records",
          description: "Process and view 80G receipts",
          path: "/auth/dashboard/paymenthistory",
          icon: <FcMoneyTransfer className="text-lg" />,
        },
        {
          name: "Contact Inquiries",
          description: "Manage help requests",
          path: "/auth/contactus",
          icon: <FcContacts className="text-lg" />,
        },
        {
          name: "Volunteer Leads",
          description: "New volunteer applications",
          path: "/auth/leads",
          icon: <FcBusinessContact className="text-lg" />,
        },
      ],
    },
    {
      title: "Content & Web",
      links: [
        {
          name: "Success Stories",
          description: "Manage children stories",
          path: "/our-stories",
          icon: <FcImageFile className="text-lg" />,
        },
        {
          name: "Analytics",
          description: "Website traffic and impact",
          path: "/auth/google-analytics",
          icon: <FcStatistics className="text-lg" />,
        },
        {
          name: "Live Website",
          description: "View shishuhriday.com",
          path: "/",
          icon: <FcGlobe className="text-lg" />,
        },
      ],
    },
  ];

  const navSections = allNavSections; // Simplified for this update

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (sidebarRef.current && !sidebarRef.current.contains(event.target)) {
        closeSidebar();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [sidebarRef]);

  const NavLink = ({ link, onClick, isDropdown = false }) => (
    <Link
      to={link.path}
      className={`flex items-center p-3 rounded-xl transition-all duration-200 group ${location.pathname === link.path
          ? "bg-[#F06C60] text-white shadow-lg shadow-red-100"
          : "text-gray-600 hover:bg-red-50 hover:text-[#F06C60]"
        } ${isDropdown ? "pl-10" : ""}`}
      onClick={onClick}
    >
      <div className="mr-3">{link.icon}</div>
      <div className="flex flex-col leading-tight">
        <span className="font-bold text-sm tracking-tight">{link.name}</span>
        <span className={`text-[10px] ${location.pathname === link.path ? 'text-white/80' : 'text-gray-400'}`}>{link.description}</span>
      </div>
    </Link>
  );

  return (
    <>
      {/* Sidebar */}
      <div
        ref={sidebarRef}
        className={`fixed top-0 z-50 left-0 pt-20 h-full bg-white border-r border-gray-100 transition-all duration-300 ease-in-out shadow-2xl shadow-gray-200
        md:translate-x-0 md:w-72
        ${isSidebarOpen ? "translate-x-0 w-72" : "-translate-x-full w-0 md:translate-x-0 md:w-72"}
      `}
      >
        <div className="flex flex-col overflow-y-auto h-[calc(100vh-140px)] px-4 py-6">
          {navSections.map((section, sectionIndex) => (
            <div key={sectionIndex} className="mb-8">
              <div className="text-[10px] font-black text-gray-400 uppercase px-3 mb-4 tracking-[0.2em]">
                {section.title}
              </div>
              <div className="space-y-2">
                {section.links.map((link) => (
                  <NavLink
                    key={link.path}
                    link={link}
                    onClick={() => {
                      if (window.innerWidth < 768) closeSidebar();
                    }}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Footer info in Sidebar */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-gray-50 bg-gray-50/50">
          <button
            onClick={handleLogout}
            className="flex items-center justify-between w-full p-4 text-red-600 hover:bg-red-600 hover:text-white rounded-2xl transition-all duration-300 font-bold text-sm shadow-sm"
          >
            <span>SIGN OUT</span>
            <IoMdLogOut className="text-xl" />
          </button>
        </div>
      </div>

      {/* Top Header */}
      <div className="fixed top-0 left-0 right-0 z-[60] bg-white border-b border-gray-100 h-20 shadow-sm">
        <div className="flex items-center justify-between h-full px-6">
          <div className="flex items-center gap-4">
            <button
              onClick={toggleSidebar}
              className="p-2 hover:bg-gray-50 rounded-xl transition-colors md:hidden text-gray-500"
            >
              <FcMenu size={24} />
            </button>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#F06C60] rounded-xl flex items-center justify-center text-white shadow-lg">
                <FcGlobe size={24} className="brightness-150" />
              </div>
              <div>
                <h2 className="text-sm font-black text-gray-900 leading-none">SHISHU HRIDAY</h2>
                <p className="text-[10px] font-bold text-red-500/70 uppercase tracking-widest mt-1">Foundation Admin</p>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-4">
            <div className="hidden sm:block text-right">
              <p className="text-sm font-bold text-gray-900 leading-none">{user?.name || 'Administrator'}</p>
              <p className="text-[10px] font-medium text-gray-400 mt-1 uppercase tracking-wider">Access Granted</p>
            </div>
            <div className="w-10 h-10 rounded-full bg-gray-100 border-2 border-white shadow-md overflow-hidden">
              <img src={`https://ui-avatars.com/api/?name=${user?.name || 'Admin'}&background=F06C60&color=fff`} alt="Profile" />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AdminNavbar;
